import { useMemo } from "react";
import clsx from "clsx";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from "recharts";

import type { FC } from "react";
import type { CustomizableChartProps } from "./types";

// Default theme colors from design system
const DEFAULT_COLORS = [
  "var(--color-primary-500)",
  "var(--color-secondary-500)",
  "var(--color-success-500)",
  "var(--color-warning-500)",
  "var(--color-error-500)",
  "var(--color-neutral-400)",
  "var(--color-primary-300)",
  "var(--color-secondary-300)",
  "var(--color-success-300)",
  "var(--color-warning-300)",
  "var(--color-error-300)",
  "var(--color-neutral-300)"
];

const CustomizableChart: FC<CustomizableChartProps> = ({
  data,
  config = {},
  className = "",
  onSegmentClick,
  onHover,
  onLegendClick,
  id
}) => {
  const {
    title = "",
    showPercentages = true,
    showLabels = true,
    colors = DEFAULT_COLORS,
    animation = { duration: 1000, easing: "ease-out" },
    responsive = false,
    size = { width: 400, height: 300, innerRadius: 0, outerRadius: 100 },
    tooltip = { show: true },
    legend = { show: true, position: "bottom", layout: "horizontal" },
    accessibility = { ariaLabel: "Chart", keyboardNavigation: true },
    style = {},
    rtl = false
  } = config;

  // Filter visible data and calculate percentages
  const processedData = useMemo(() => {
    const visibleData = data.filter((item) => item.visible !== false);
    const total = visibleData.reduce((sum, item) => sum + item.value, 0);

    return visibleData.map((item, index) => ({
      ...item,
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : "0",
      fill: item.color || colors[index % colors.length],
      originalIndex: index
    }));
  }, [data, colors]);

  // Custom tooltip formatter
  const tooltipFormatter = (value: number, name: string, props: unknown) => {
    const item = processedData.find((d) => d.name === name);
    if (!item) return [value, name];

    const percentage = item.percentage;
    const customFormatter = tooltip.formatter;

    if (customFormatter) return customFormatter(value, name, props);

    return showPercentages
      ? [`${value} (${percentage}%)`, name]
      : [value, name];
  };

  // Custom label renderer for percentages
  const renderLabel = (entry: { percentage: string }) => {
    if (!showLabels || !showPercentages) return null;
    return `${entry.percentage}%`;
  };

  // Handle segment click
  const handleClick = (data: { name: string }) => {
    if (onSegmentClick && data) {
      const originalItem = processedData.find(
        (item) => item.name === data.name
      );

      if (originalItem) {
        onSegmentClick(originalItem);
      }
    }
  };

  // Handle hover
  const handleMouseEnter = (data: { name: string }) => {
    if (onHover && data) {
      const originalItem = processedData.find(
        (item) => item.name === data.name
      );
      onHover(originalItem || null);
    }
  };

  const handleMouseLeave = () => {
    if (onHover) {
      onHover(null);
    }
  };

  const chartContent = (
    <PieChart
      width={size.width ?? 520}
      height={size.height ?? 400}
      className={clsx("min-w-full font-sans", rtl && "rtl")}
      barSize={20}
      style={{
        fontFamily: "inherit",
        minWidth: "100%",
        ...style.chart
      }}
      aria-label={accessibility.ariaLabel}
    >
      <Pie
        data={processedData}
        cx="50%"
        cy="50%"
        innerRadius={size.innerRadius}
        outerRadius={size.outerRadius}
        dataKey="value"
        animationBegin={animation.delay || 0}
        animationDuration={animation.duration}
        animationEasing="ease-out"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        label={renderLabel}
        labelLine={false}
      >
        {processedData.map((entry) => (
          <Cell
            key={`cell-${entry.name}`}
            fill={entry.fill}
            stroke="rgb(229 231 235)"
            strokeWidth={2}
            style={{
              filter:
                "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
              transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              cursor: "pointer",
              transformOrigin: "center center"
            }}
            className="hover:drop-shadow-2xl hover:scale-110 hover:-translate-y-2 hover:rotate-1 active:scale-95 active:-translate-y-1"
          />
        ))}

        {/* <Label
          value={data[0].value}
          position="insideBottomLeft"
          fill="grey"
          style={{
            fontSize: "32px",
            fontWeight: "bold",
            fontFamily: "Roboto"
          }}
        /> */}
      </Pie>

      {tooltip.show && (
        <Tooltip
          formatter={tooltipFormatter}
          content={tooltip.customContent}
          contentStyle={{
            backgroundColor: "rgb(249 250 251)", // gray-50
            border: "1px solid rgb(229 231 235)", // gray-200
            borderRadius: "8px", // rounded-lg
            fontSize: "14px",
            boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
            ...(rtl && { direction: "rtl", textAlign: "right" })
          }}
        />
      )}

      {legend.show && (
        <Legend
          wrapperStyle={{
            fontSize: "14px",
            color: "rgb(75 85 99)", // gray-600
            marginTop: "16px",
            ...(rtl && { direction: "rtl", textAlign: "right" }),
            ...style.legend
          }}
          layout={legend.layout}
          verticalAlign={legend.verticalAlign}
          align={(() => {
            if (legend.align === "start") return "left";
            if (legend.align === "end") return "right";
            return "center";
          })()}
          onClick={(data) => {
            if (onLegendClick) {
              const originalItem = processedData.find(
                (item) => item.name === data.value
              );
              if (originalItem) {
                onLegendClick(originalItem);
              }
            }
          }}
        />
      )}
    </PieChart>
  );

  return (
    <div
      id={id}
      className={clsx("flex", className)}
      style={{
        perspective: "1000px",
        transformStyle: "preserve-3d",
        ...style.container
      }}
      aria-label={accessibility.ariaLabel}
      aria-describedby={
        accessibility.ariaDescription ? `${id}-description` : undefined
      }
    >
      {accessibility.ariaDescription && (
        <div id={`${id}-description`} className="sr-only">
          {accessibility.ariaDescription}
        </div>
      )}

      {title && (
        <h3
          className={clsx(
            "text-lg font-semibold text-gray-800 mb-2",
            rtl && "text-right"
          )}
        >
          {title}
        </h3>
      )}

      {responsive ? (
        <ResponsiveContainer width="100%" height={size.height}>
          {chartContent}
        </ResponsiveContainer>
      ) : (
        chartContent
      )}
    </div>
  );
};

export default CustomizableChart;
